import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { router } from '@/router'
import { useNowPlaying } from '@/composables/useNowPlaying'
import { useAudioBook } from '@/composables/useAudioBook'
import { ttsSupported } from '@/ai/tts'
import { useSettingsStore } from '@/stores/settings'
import { useStatsStore } from '@/stores/stats'
import { resolveChunkCfi } from '@/composables/useCfiResolver'
import * as repo from '@/db/repositories'
import { useToaster } from '@/composables/useToaster'
import { humanizeAiError } from '@/ai/errors'
import { t } from '@/i18n'
import { useSearchStore } from '@/stores/search'
import { useLibraryStore } from '@/stores/library'

/**
 * Reproductor de audio global (modo audiolibro).
 *
 * Única fuente de verdad del estado de reproducción. NO habla ni sintetiza:
 * delega en un `PlaybackEngine` intercambiable. Hay dos motores:
 *
 *   - 'browser': adaptador sobre `useTts` (SpeechSynthesis + karaoke de
 *     foliate). Gratis, pero necesita la vista del lector montada, así que
 *     al desmontarse `ReaderView` el motor se desconecta y el store pausa.
 *   - 'neural': cola de audio real sobre la tabla `bookIndex` (Task 7).
 *     Sobrevive a la navegación porque no depende de foliate.
 *
 * El store vive en `App.vue`, no en el lector: por eso la escucha sigue al
 * navegar a la biblioteca.
 */

/** Cuál de los dos motores está activo. */
export type AudioEngineId = 'browser' | 'neural'

export type AudioStatus = 'idle' | 'loading' | 'playing' | 'paused'

/**
 * Modo del temporizador de sueño. 'chapter' para al terminar el capítulo en
 * curso; 'minutes' a los N minutos.
 *
 * ponytail: no se persiste entre recargas a propósito. Un temporizador de
 * sueño que sobrevive a reabrir la app es una sorpresa desagradable.
 */
export type SleepMode = 'off' | 'minutes' | 'chapter'

/**
 * Contrato mínimo que el store necesita de un motor de voz. `useTts` lo
 * cumple estructuralmente salvo `setRate` (su `rate` es un Ref), así que
 * `ReaderView` le pasa un adaptador en vez del objeto crudo.
 */
export interface PlaybackEngine {
  play(): Promise<void>
  pause(): void
  resume(): Promise<void>
  next(): Promise<void>
  prev(): Promise<void>
  stop(): void
  setRate(value: number): void
  /**
   * Salta `seconds` (relativo y con signo) dentro del audio en curso.
   *
   * OPCIONAL a propósito: el motor navegador no puede cumplirlo —
   * `speechSynthesis` no expone la posición de lo que está diciendo, así que
   * no hay nada que mover— y fingir que sí (un método vacío) daría un botón
   * que miente. Quien lo ofrezca en la UI debe mirar antes `seekSupported`.
   */
  seek?(seconds: number): void
}

/**
 * Salto por defecto, en segundos (PRD: ±15 s). Lo usan los botones de la UI y
 * los controles del sistema cuando la acción no trae su propio `seekOffset`.
 */
export const SEEK_SECONDS = 15

export const useAudioPlayerStore = defineStore('audioPlayer', () => {
  const bookId = ref<string | null>(null)
  const engineId = ref<AudioEngineId>('browser')
  const status = ref<AudioStatus>('idle')
  const rate = ref(1)

  const nowPlaying = useNowPlaying()
  const toaster = useToaster()
  const search = useSearchStore()
  const library = useLibraryStore()
  let handlersWired = false

  const stats = useStatsStore()
  /** True si la sesión de stats abierta la abrimos nosotros (no el lector). */
  let ownsSession = false

  /**
   * Intención de escucha del usuario: true entre un `play()` real y el
   * siguiente `pause()`/`stop()`/desconexión forzada. A diferencia de
   * `status.value === 'playing'`, **sobrevive** a que `status` pase por
   * `'loading'` (Task 10: cargar+indexar la cola neural puede tardar). Sin
   * esto, un `setEngine` que arranca mientras otro sigue cargando (p. ej.
   * el usuario toca el selector de motor dos veces seguidas) leería
   * `status.value === 'loading'` y concluiría —erróneamente— que no sonaba
   * nada, dejando el reproductor mudo con aspecto de "pausado" en vez de
   * retomar la escucha real.
   */
  let listening = false

  /**
   * Motor cuyo estado interno sigue vivo (reproduciendo o en pausa), es
   * decir: el único al que tiene sentido pedirle `resume()`.
   *
   * Los motores no exponen su estado interno, y `resume()` es un **no-op
   * silencioso** en ambos si ese estado no es "pausado" (`useTts.resume()`
   * sale si su ref no está en 'paused'; el neural no tiene `audio.src` que
   * reanudar). Como `setEngine` para el motor saliente con `stop()` —no con
   * `pause()`— y un motor recién conectado nunca ha hablado, hay caminos en
   * los que el reproductor queda en 'paused' con TODOS los motores en
   * 'idle': ahí el único botón que ofrece la UI («reanudar») no devolvería
   * audio, dejando el estado en 'playing' con el icono de pausa y silencio
   * absoluto. Este es el bug del patrón «parar + reanudar», que ha
   * reaparecido cuatro veces en esta feature con distinta forma.
   *
   * Con esta marca, `resume()` sabe cuándo reanudar de verdad y cuándo
   * tiene que ARRANCAR (`play()`), que no asume nada sobre el estado
   * previo. Ver el test parametrizado de `audioPlayer.test.ts` que recorre
   * `{sonando, pausado} × {navegador→neural, neural→navegador}`.
   */
  let startedEngine: AudioEngineId | null = null

  /**
   * Lo que el store necesita del motor neural más allá de `PlaybackEngine`.
   * Se declara aquí en vez de importar `AudioBookEngine` para no acoplar el
   * store al composable (que ya importa `PlaybackEngine` de este fichero).
   */
  type NeuralEngine = { load(id: string): Promise<number>; queuedBook(): string | null }

  /**
   * El motor neural, si está conectado. `useAudioBook` conserva los
   * fragmentos del último `load()` y su `play()` solo carga si la cola está
   * vacía, así que hay que saber de quién es la cola: sin eso, escuchar un
   * segundo libro con el motor neural ya activo seguiría sonando —y
   * persistiendo el progreso de— el libro anterior.
   *
   * La respuesta se le pide al motor (`queuedBook()`) en vez de llevar la
   * cuenta aquí: el store no puede saber si una carga llegó a aplicarse (una
   * abandonada devuelve su cuenta igual), y cada intento de deducirlo desde
   * fuera ha reabierto el mismo bug por otra puerta.
   */
  function neuralEngine(): NeuralEngine | undefined {
    return engines.neural as NeuralEngine | undefined
  }

  /**
   * Contador de secuencia de `play`, mismo patrón que `engineChangeToken` y
   * `progressToken`. Cargar la cola neural de un libro puede tardar (lee
   * `bookIndex` y, si hace falta, indexa el EPUB al vuelo), y en esa ventana
   * el usuario puede pedir otro libro —o volver al anterior—. Cada llamada se
   * numera al entrar; solo la que sigue siendo la última emitida cuando su
   * carga resuelve llega a tocar el estado.
   *
   * Ojo: esto NO ordena los `load()` que ya están dentro del motor (dos
   * cargas concurrentes pueden resolver en desorden y dejarle la cola del
   * libro equivocado). De eso se encarga `useAudioBook` con su propio
   * contador de generación, que es donde vive la cola.
   */
  let playToken = 0

  /**
   * objectURL de la portada actualmente en la MediaSession. Se guarda para
   * poder revocarlo: sin esto, cada libro escuchado deja una URL colgando
   * (mismo patrón que `BookCover.vue`/`LibraryView.vue`).
   */
  let artworkUrl: string | null = null

  /**
   * Altura real (px) de `AudioMiniBar`, medida por ella misma con
   * `ResizeObserver`. Única fuente de verdad para que `Toaster` desplace los
   * toasts justo por encima sin inventar un número que quede corto.
   */
  const miniBarHeight = ref(0)

  const sleepMode = ref<SleepMode>('off')
  const sleepMinutes = ref(30)
  let sleepTimer: ReturnType<typeof setTimeout> | null = null
  /** Capítulo de referencia del modo 'chapter' (href del primer aviso). */
  let sleepChapterHref: string | null = null

  /** Posición y total de fragmentos (solo motor neural). */
  const position = ref(0)
  const total = ref(0)
  /** Id del fragmento que suena, para que Task 9 persista el progreso. */
  const chunkId = ref<string | null>(null)
  /** Href del capítulo en curso, para el temporizador y la UI. */
  const chapterHref = ref<string | null>(null)

  /** Motores conectados. Solo el de `engineId` recibe órdenes. */
  const engines: Partial<Record<AudioEngineId, PlaybackEngine>> = {}

  /**
   * ¿Hay un motor conectado para el `engineId` actual? `engines` es un
   * objeto plano (no reactivo) a propósito — los motores traen funciones,
   * no tiene sentido envolverlos en un Proxy. Esta bandera existe solo para
   * que la UI (mini-barra) sepa cuándo un play/resume no tiene con qué
   * cumplir, en vez de ofrecer un botón que no hace nada en silencio (p.
   * ej. tras salir del lector: `ReaderView` desconecta el motor navegador
   * al desmontarse, y sin él no hay con qué reanudar).
   */
  const engineConnected = ref(false)

  /**
   * ¿Puede el motor activo saltar dentro del audio (±15 s)? Solo el neural:
   * reproduce audio real y tiene una posición que mover. El del navegador no
   * implementa `seek` porque `speechSynthesis` no expone ninguna.
   *
   * La consumen la mini-barra y `TtsControls` para deshabilitar los botones
   * —diciendo por qué— en vez de ofrecer un ±15 s mudo, y `syncEngineFlags`
   * la usa para retirar además los botones de salto de la pantalla de
   * bloqueo, donde no hay forma de explicarle nada al usuario.
   */
  const seekSupported = ref(false)

  /**
   * ¿Debe verse la mini-barra global? Visible en cualquier ruta menos el
   * lector (allí manda `TtsControls`, y duplicar controles confunde) y solo
   * mientras hay una reproducción con libro asociado. Única fuente de verdad:
   * la consumen tanto `AudioMiniBar` (para pintarse) como `Toaster` (para
   * saber si debe desplazarse por encima de ella).
   */
  const isMiniBarVisible = computed(
    () => status.value !== 'idle' && !!bookId.value && router.currentRoute.value.name !== 'reader',
  )

  function current(): PlaybackEngine | null {
    return engines[engineId.value] ?? null
  }

  /**
   * Recalcula lo que la UI (la de la app y la del sistema operativo) puede
   * ofrecer con el motor que hay ahora en `engineId`. Se llama en TODOS los
   * puntos donde eso cambia: conectar, desconectar y cambiar de motor.
   *
   * `engines` es un objeto plano, no reactivo (los motores traen funciones, no
   * tiene sentido envolverlos en un Proxy), así que estas banderas no pueden
   * ser `computed`: hay que refrescarlas a mano. Van juntas en una sola
   * función para que no se pueda actualizar una y olvidar la otra.
   */
  function syncEngineFlags() {
    const engine = engines[engineId.value]
    engineConnected.value = !!engine
    seekSupported.value = typeof engine?.seek === 'function'
    // Pantalla de bloqueo y auriculares: si el motor no sabe saltar, se
    // retiran esos botones (el sistema deja de pintarlos) en vez de dejarlos
    // puestos sin hacer nada.
    nowPlaying.setSeekHandlers(
      seekSupported.value
        ? {
            backward: (offset) => seek(-(offset ?? SEEK_SECONDS)),
            forward: (offset) => seek(offset ?? SEEK_SECONDS),
          }
        : null,
    )
  }

  /**
   * Registra un motor. Lo llama `ReaderView` al montar (motor navegador) y
   * el propio store al preparar el motor neural.
   */
  function attachEngine(id: AudioEngineId, impl: PlaybackEngine) {
    engines[id] = impl
    // Instancia nueva: nunca ha hablado, así que no hay nada que reanudar en
    // ella (p. ej. `ReaderView` crea un `useTts` fresco en cada montaje).
    if (startedEngine === id) startedEngine = null
    // El usuario pudo cambiar la velocidad antes de que existiera el motor.
    impl.setRate(rate.value)
    syncEngineFlags()
  }

  /**
   * Desconecta un motor. Si era el que sonaba, pausa: dejar el estado en
   * 'playing' sin nadie que hable sería mentirle a la UI.
   */
  function detachEngine(id: AudioEngineId) {
    const impl = engines[id]
    if (!impl) return
    if (engineId.value === id) {
      if (status.value === 'playing') {
        impl.pause()
        status.value = 'paused'
        listening = false
        nowPlaying.setPlaybackState('paused')
        // La pantalla puede apagarse mientras está en pausa; ver el comentario
        // de `pause()` sobre por qué esto no es un `release()` completo.
        nowPlaying.releaseWakeLock()
      }
      // El motor que se desconecta es el activo (`engineId.value`): la
      // sesión de escucha que el temporizador vigilaba termina aquí, tanto
      // si sonaba como si ya estaba en pausa. Esto pasa en cada navegación
      // fuera del lector con el motor navegador (`ReaderView` desconecta
      // con `detachEngine` al desmontarse, sin pasar por `stop()`), y el
      // store sobrevive para el próximo libro que se conecte. Sin este
      // desarme, un temporizador de minutos programado en el libro A
      // dispara `pause()` sobre la sesión del libro B en un instante
      // arbitrario, y uno de capítulo compara el href de A contra el
      // primer aviso de B y pausa sin motivo real. Si el motor que se
      // desconecta NO es el activo (p. ej. se libera el neural mientras
      // suena el navegador), no se toca: no hay sesión interrumpida.
      clearSleepTimer()
      // PRD (motor navegador): sin la vista foliate montada no hay con qué
      // reanudar. Avisar en vez de dejar un play mudo — solo si había una
      // sesión real que retomar (bookId asignado y no ya 'idle'; si nunca
      // se llegó a escuchar nada, esto pasa en cada desmontaje normal de
      // ReaderView y no hay nada que avisar).
      if (id === 'browser' && bookId.value && status.value !== 'idle') {
        notifyBrowserEngineGone()
      }
    }
    // El estado interno del motor se va con él: lo que quede después no es
    // reanudable (ver `startedEngine`).
    if (startedEngine === id) startedEngine = null
    delete engines[id]
    // Después del `delete`, no antes: las banderas se calculan mirando lo que
    // queda en `engines`.
    syncEngineFlags()
  }

  /**
   * Deja el reproductor como lo dejaría un `pause()` real, sin pedirle nada
   * al motor: es el aterrizaje de todos los caminos que no llegan a sonar
   * (sin motor con el que cumplir, cola que el motor no montó, 'loading' que
   * no puede quedarse pegado).
   *
   * Se conserva `bookId`, y esa es toda la promesa: el botón de play de la UI
   * llama a `resume()`, que detecta que no hay nada que reanudar —ni estado
   * interno del motor ni cola de este libro— y deriva a `play()`, que vuelve
   * a cargar. Sin esa derivación esto sería un callejón sin salida con el
   * icono de "sonando" y solo se saldría con la ✕.
   */
  function settleAsPaused() {
    status.value = 'paused'
    listening = false
    nowPlaying.setPlaybackState('paused')
    // Solo el wake lock, igual que `pause()`: la MediaSession sigue viva.
    nowPlaying.releaseWakeLock()
  }

  async function play(id: string) {
    if (!current()) return
    const token = ++playToken
    const engineToken = engineChangeToken
    // Cambio de libro con una sesión NUESTRA abierta: se cierra la de antes y
    // más abajo se abre la del nuevo. Sin esto, el guard de sesión única veía
    // una activa y no abría otra, así que el tiempo de escuchar B se anotaba
    // en la sesión de A y su `percentEnd` acababa siendo el de B.
    //
    // Va aquí arriba del todo porque `bookId`/`position` son todavía los de A
    // hasta la línea siguiente, y es el progreso que hay que guardarle. Si la
    // sesión activa es del LECTOR no se toca: sigue mandando él (por eso
    // `ownsSession`).
    //
    // Sin `await` a propósito: `play()` tiene que dejar `bookId` y `status`
    // puestos de forma SÍNCRONA —la mini-barra y los tests de carrera cuentan
    // con ello— y no hace falta esperar, porque `endSession` suelta
    // `activeBookId` antes de persistir. Es justo lo que le faltaba y lo que
    // hacía que parar y escuchar otro libro perdiera la segunda sesión.
    if (ownsSession && stats.activeBookId !== null && stats.activeBookId !== id) {
      void stats.endSession(percentFromPosition())
      ownsSession = false
    }
    bookId.value = id
    // Cambio de libro con el motor neural ya activo (escuchar A, salir a la
    // biblioteca, abrir B): la cola cargada sigue siendo la de A. Sin
    // recargar aquí sonaría A —y `persistNeuralProgress` escribiría el
    // progreso de A— con la metadata de B, porque `useAudioBook.play()` solo
    // carga si su cola está vacía y `engineId` no se reinicia entre libros.
    if (engineId.value === 'neural' && neuralEngine()?.queuedBook() !== id) {
      status.value = 'loading'
      // Obsoleto si entretanto se pidió reproducir otra vez, aunque sea el
      // MISMO libro: `bookId.value` puede volver a `id` tras pasar por otro
      // (A→B→A) y esta respuesta seguiría siendo de un intento abandonado,
      // que reaplicaría `total` y devolvería `position` a 0 en mitad de la
      // escucha. Por eso el token propio y no `bookId.value !== id`. Y también
      // obsoleto si se cambió de motor: ese `setEngine` paró el motor neural,
      // que con eso abandona la carga en vuelo — aplicar aquí su cuenta sería
      // hablar de una cola que el motor no llegó a montar.
      const count = await loadNeuralQueue(
        id,
        () => token !== playToken || engineToken !== engineChangeToken,
      )
      if (count === null) return
      // Y se vuelve a preguntar AHORA, después de esperar: `count` dice
      // cuántos fragmentos tiene el libro, no que el motor los tenga montados
      // (una carga que él abandonó devuelve su cuenta igual). Preguntar solo
      // antes del `await` deja abierta la puerta de siempre: arrancar aquí
      // sonaría el libro anterior con el título y la portada de este,
      // sintetizando fragmento tras fragmento hasta que el usuario pare.
      if (count > 0 && neuralEngine()?.queuedBook() !== id) {
        // Nadie más va a arreglar el estado (esta carga no la abandonó un
        // intento más nuevo, o el `stale()` de arriba ya habría salido): se
        // deja recuperable —el usuario pulsa play y se reintenta— en vez de
        // 'loading' para siempre.
        settleAsPaused()
        return
      }
      total.value = count
      position.value = 0
      chunkId.value = null
      if (count === 0) {
        // El EPUB no da texto extraíble: se cae al motor navegador y se
        // avisa, igual que en `setEngine`.
        engineId.value = 'browser'
        syncEngineFlags()
        toaster.info(t('audio.neuralNoText'), 8000)
      }
    }
    // `current()` se relee: el fallback de arriba pudo cambiar de motor.
    const engine = current()
    if (!engine) {
      // Fallback al navegador sin lector montado: no hay con qué sonar. No
      // dejar 'loading' pegado ni mentir con un 'playing' mudo.
      if (status.value === 'loading') settleAsPaused()
      return
    }
    // Guard de sesión única: si el lector ya abrió una, no abrimos otra o el
    // tiempo se contaría dos veces.
    if (stats.activeBookId === null) {
      // Desde el progreso guardado, igual que `ReaderView`. `endSession`
      // anota `pagesRead = percentEnd - percentStart`, así que con un 0 fijo
      // cada sesión de escucha se atribuía TODO el progreso del libro:
      // escuchar dos minutos de uno que iba por el 60 % sumaba 62 puntos a la
      // racha y a la meta diaria en vez de 2. `library.find` es síncrono (el
      // catálogo ya está en memoria; es de donde la mini-barra saca el
      // título), así que esto no abre ninguna ventana de espera nueva.
      stats.startSession(id, library.find(id)?.progress.percent ?? 0)
      ownsSession = true
    }
    status.value = 'playing'
    listening = true
    if (!handlersWired) {
      // Los controles del sistema disparan las mismas acciones que la UI.
      nowPlaying.setHandlers({
        play: () => { void resume() },
        pause: () => pause(),
        next: () => { void next() },
        prev: () => { void prev() },
      })
      handlersWired = true
    }
    nowPlaying.setPlaybackState('playing')
    // El motor navegador necesita el truco del silencio + wake lock; el
    // neural reproduce audio real y ya sostiene la sesión de medios.
    await nowPlaying.acquire({ silentKeepAlive: engineId.value === 'browser' })
    // PRD: la pantalla de bloqueo y los controles del sistema deben mostrar
    // título, autor y portada — no un reproductor anónimo.
    await applyNowPlayingMetadata(id)
    // Mismo guard que el de `resume()`, por el mismo motivo: estos dos `await`
    // son espera real (el `<audio>` de keep-alive con el motor navegador, dos
    // lecturas de IndexedDB para la metadata), y `status`/`listening` se
    // declararon ANTES. En esa ventana caben la pausa, el temporizador de
    // sueño, salir del lector y la ✕ — ninguno toca los contadores, así que
    // sin mirar también el estado el motor se pone a hablar con el reproductor
    // diciendo 'paused' (o 'idle' y sin mini-barra con la que pararlo, que con
    // el motor neural es dinero real sintetizándose a solas). Y el centinela
    // de wake lock que `acquire()` acaba de pedir nace huérfano: quien pausó
    // soltó el anterior antes de que este existiera.
    if (
      token !== playToken ||
      engineToken !== engineChangeToken ||
      status.value !== 'playing'
    ) {
      if (status.value !== 'playing') nowPlaying.releaseWakeLock()
      return
    }
    // A partir de aquí este motor sí tiene estado interno vivo: es al único
    // al que un `resume()` posterior puede pedirle nada (ver `startedEngine`).
    startedEngine = engineId.value
    try {
      await engine.play()
    } catch (e) {
      // El motor no llegó a arrancar: `useTts.play()` pasa por `initTTS` y
      // `ttsStart()` de foliate, que pueden fallar sobre una vista que ya no
      // está en condiciones. Quedarse en 'playing' sería un reproductor mudo
      // con el icono de pausa y el wake lock cogido —la pantalla encendida sin
      // que suene nada—, así que se aterriza como un pause real. Y el motor no
      // tiene estado que reanudar, así que el próximo play ARRANCA.
      console.warn('[audio] el motor no pudo arrancar:', e)
      startedEngine = null
      settleAsPaused()
    }
  }

  function pause() {
    // En 'loading' todavía no hay nada que pausar —la reproducción viene de
    // camino— pero sí algo que cancelar: la carga de la cola, que con la
    // indexación al vuelo (Task 10) puede ser reparsear el EPUB entero. Subir
    // `playToken` deja obsoleto ese intento por el mismo mecanismo que usa la
    // ✕, y como `loadNeuralQueue` comprueba `stale()` ANTES de indexar,
    // pausar a tiempo se ahorra el trabajo caro además del audio.
    //
    // Es cancelar, no parar: se conserva `bookId` y se aterriza en
    // `settleAsPaused()`, así que el botón de play sigue ahí y `resume()`
    // deriva en `play()`, que vuelve a cargar. Sin esto, `pause()` salía por
    // el guard de abajo y la escucha arrancaba igual al resolver la carga:
    // pulsar pausa durante «Preparando…» no hacía nada.
    if (status.value === 'loading') {
      playToken++
      settleAsPaused()
      return
    }
    if (status.value !== 'playing') return
    current()?.pause()
    status.value = 'paused'
    listening = false
    nowPlaying.setPlaybackState('paused')
    // Solo el wake lock: el `<audio>` silencioso sigue sonando para que la
    // MediaSession no muera y los controles de la pantalla de bloqueo no
    // desaparezcan. `resume()` vuelve a pedir el wake lock vía `acquire()`.
    nowPlaying.releaseWakeLock()
  }

  async function resume() {
    if (status.value !== 'paused') return
    const engine = current()
    if (!engine) return
    // Hay dos motivos para no poder REANUDAR y tener que ARRANCAR, y los dos
    // acaban en un `resume()` que es un no-op silencioso si no se detectan:
    //
    //   1. El motor actual no conserva un estado interno pausado (se le paró
    //      con `stop()` al cambiar de motor, o es una instancia recién
    //      conectada). Es el caso «pausa → cambio de motor → vuelta → play»
    //      del patrón «parar + reanudar».
    //   2. El motor neural no tiene montada la cola de este libro (una carga
    //      que se abandonó a mitad, ver `settleAsPaused`). Reanudar no
    //      recarga; `play()` sí, y además sabe qué hacer si el libro resulta
    //      no tener texto (caer al navegador y avisar) en vez de dejar que el
    //      motor se encuentre una cola vacía y dé el libro por terminado.
    //
    // `play()` no asume nada sobre el estado previo, así que sirve para los
    // dos.
    const queueIsAlien =
      engineId.value === 'neural' && neuralEngine()?.queuedBook() !== bookId.value
    if ((startedEngine !== engineId.value || queueIsAlien) && bookId.value) {
      await play(bookId.value)
      return
    }
    // Este `resume` también manda sobre la reproducción, así que se numera
    // como un `play` (mismo contador: `stop()` y cualquier `play()` posterior
    // lo dejan obsoleto de un plumazo) y se anota el motor de partida.
    const token = ++playToken
    const engineToken = engineChangeToken
    status.value = 'playing'
    listening = true
    nowPlaying.setPlaybackState('playing')
    await nowPlaying.acquire({ silentKeepAlive: engineId.value === 'browser' })
    // Sobrevivir a un `await` obliga a recomprobar. En esa ventana pudo
    // entrar un `play('otro-libro')` —que ya llamó a `engine.load()`, dejando
    // la cola invalidada y el `<audio>` sin `src`—, un `setEngine()` o la ✕.
    // Reanudar ahora sería peor que no hacer nada: sin `src`, el motor neural
    // deriva en su propio `play()`, recarga y arranca por su cuenta; el
    // `play()` del store se encuentra la cola robada, aterriza en
    // `settleAsPaused()` y el reproductor acaba diciendo 'paused' con el audio
    // sonando (o resucitando la escucha después de la ✕). Manda el más nuevo.
    //
    // Y el estado, no solo los tokens: `status` se puso en 'playing' ANTES del
    // `await`, así que todo lo que pausa mirando ese 'playing' —`pause()`, el
    // temporizador de sueño, `detachEngine()` al salir del lector,
    // `settleAsPaused()`— deja 'paused' sin tocar ningún contador. Con el
    // motor navegador la espera es real (el `<audio>` de keep-alive), así que
    // la ventana da de sobra para pulsar pausa: sin esta comprobación el motor
    // se pone a hablar con el reproductor diciendo 'paused'.
    if (
      token !== playToken ||
      engineToken !== engineChangeToken ||
      status.value !== 'playing'
    ) {
      // Y si además ya no escucha nadie (pausa, temporizador de sueño, salir
      // del lector, ✕), el centinela de wake lock que `acquire()` acaba de
      // pedir nace huérfano: quien pausó soltó el anterior ANTES de que este
      // existiera. Sin esto, el reproductor se queda en 'paused' con la
      // pantalla encendida hasta el próximo pause/stop — o sea, toda la noche
      // en el caso «me duermo escuchando». Si el estado sigue en 'playing' es
      // que manda otra operación más nueva, y el lock vivo es suyo: no se toca.
      if (status.value !== 'playing') nowPlaying.releaseWakeLock()
      return
    }
    await engine.resume()
  }

  /**
   * Salta `seconds` dentro del audio en curso: relativo y con signo
   * (−15 al retroceder, +15 al avanzar; PRD, historia P0 nº 2).
   *
   * Delegación OPCIONAL: solo el motor neural la implementa. Con la voz del
   * navegador esto es un no-op, y por eso la UI no debe llegar aquí — mira
   * antes `seekSupported` y deshabilita los botones explicando el motivo.
   * El `?.` es la red de seguridad, no la interfaz.
   *
   * Cuánto se mueve de verdad lo decide el motor: el salto no cruza el límite
   * del fragmento, y el que no cabe en lo que queda por sonar no se hace (ver
   * `useAudioBook.seek`, que explica por qué acotar no bastaba).
   */
  function seek(seconds: number) {
    current()?.seek?.(seconds)
  }

  async function next() {
    await current()?.next()
  }

  async function prev() {
    await current()?.prev()
  }

  function stop() {
    current()?.stop()
    // Parar deja obsoleto cualquier `play()` que siguiera esperando su cola:
    // si no, al resolver abriría sesión de stats, se pondría en 'playing' y
    // arrancaría el motor — con `bookId` ya en `null`, así que ni la
    // mini-barra se vería para volver a pararlo, y sintetizando dinero.
    playToken++
    // `stop()` deja el estado interno del motor en 'idle', no en 'paused'.
    startedEngine = null
    // Solo cerramos la sesión que abrimos nosotros: si la abrió el lector,
    // es cosa suya cerrarla.
    if (ownsSession) {
      void stats.endSession(percentFromPosition())
      ownsSession = false
    }
    clearSleepTimer()
    nowPlaying.release()
    releaseArtworkUrl()
    status.value = 'idle'
    listening = false
    bookId.value = null
  }

  function setRate(value: number) {
    rate.value = value
    current()?.setRate(value)
  }

  /**
   * Reclama para el reproductor la sesión de stats activa de este libro, si
   * la hay. La llama `ReaderView` al desmontarse cuando el motor neural
   * sigue sonando: la sesión la abrió el lector (no el reproductor, así que
   * `ownsSession` seguía en `false`), pero el motor neural está pensado
   * para sobrevivir a la navegación — si el lector la cerrara igualmente al
   * desmontarse (como hacía antes), el tiempo escuchado tras salir del
   * lector dejaría de contar hasta el próximo `stop()`. Tras adoptarla,
   * `stop()` la cierra como cualquier otra que hubiera abierto él mismo.
   */
  function adoptSession() {
    if (stats.activeBookId === bookId.value) ownsSession = true
  }

  /**
   * Suelta la propiedad de la sesión de stats, si la tenía. La llama
   * `ReaderView` al montarse (ronda 3) cuando reclama para sí una sesión
   * activa de OTRO libro que el reproductor había adoptado: esa sesión se
   * cierra con `endSession` antes de abrir la del libro nuevo, así que el
   * reproductor ya no puede seguir creyéndose su dueño (`stop()` no debe
   * intentar cerrarla de nuevo más tarde — ya no existe).
   *
   * Por invariante, `ownsSession` solo es `true` cuando `bookId.value`
   * coincide con el libro de la sesión que el reproductor cree tener
   * adoptada (`adoptSession`/`play` solo lo ponen a `true` bajo esa misma
   * condición), así que soltar aquí sin comprobar nada más es seguro: si no
   * tenía nada adoptado, no hace nada; si tenía adoptada justo la sesión
   * que se está cerrando, la suelta.
   */
  function releaseSession() {
    ownsSession = false
  }

  /** Llamada por `AudioMiniBar` cada vez que su altura real cambia. */
  function setMiniBarHeight(value: number) {
    miniBarHeight.value = value
  }

  /** Cancela el temporizador pendiente y vuelve a 'off'. */
  function clearSleepTimer() {
    if (sleepTimer !== null) {
      clearTimeout(sleepTimer)
      sleepTimer = null
    }
    sleepChapterHref = null
    sleepMode.value = 'off'
  }

  /**
   * Programa el temporizador de sueño. `minutes` solo aplica a
   * `mode === 'minutes'`.
   */
  function setSleepTimer(mode: SleepMode, minutes = sleepMinutes.value) {
    clearSleepTimer()
    if (mode === 'off') return
    sleepMode.value = mode
    if (mode === 'minutes') {
      sleepMinutes.value = minutes
      sleepTimer = setTimeout(() => {
        pause()
        clearSleepTimer()
      }, minutes * 60 * 1000)
    }
    // 'chapter' no usa timer: espera el aviso de notifyChapter().
  }

  /**
   * Aviso de en qué capítulo va la reproducción. Lo llama el motor: el
   * navegador desde el progreso de foliate, el neural al cambiar de chunk.
   * El primer aviso fija la referencia; un href distinto cierra el modo.
   */
  function notifyChapter(href: string | null) {
    if (sleepMode.value !== 'chapter') return
    if (sleepChapterHref === null) {
      sleepChapterHref = href
      return
    }
    if (href !== sleepChapterHref) {
      pause()
      clearSleepTimer()
    }
  }

  const settings = useSettingsStore()

  /** ¿Se puede usar la voz neural con la config de IA actual? */
  const neuralAvailable = computed(() => ttsSupported(settings.settings.ai))

  /**
   * PRD (motor navegador): «al desmontarse ReaderView con este motor
   * activo, el store pausa y emite un toast ofreciendo cambiar a neural».
   * Si la config de IA no permite neural, no se ofrece algo imposible: se
   * ofrece lo único que sí reanuda, volver al lector.
   */
  function notifyBrowserEngineGone() {
    const id = bookId.value
    if (neuralAvailable.value) {
      toaster.info(t('audio.browserEngineGone'), 8000, {
        label: t('audio.switchToNeural'),
        onClick: async () => {
          // `id` es el libro de cuando se emitió el aviso. El toast vive
          // 8 s: si entretanto el reproductor pasó a otro libro, cumplir la
          // acción sonaría el equivocado (misma familia que recargar la cola
          // neural al cambiar de libro).
          if (!id || bookId.value !== id) return
          await setEngine('neural')
          await play(id)
        },
      })
    } else {
      toaster.info(t('audio.browserEngineGoneNoNeural'), 8000, {
        label: t('audio.backToReader'),
        onClick: () => {
          if (id) void router.push({ name: 'reader', params: { bookId: id } })
        },
      })
    }
  }

  /**
   * Contador de secuencia de `setEngine`, mismo patrón que `progressToken`
   * de más abajo. `setEngine('neural')` puede quedar en vuelo un buen rato
   * (carga + indexación al vuelo, Task 10), y mientras tanto el usuario
   * puede alternar de motor otra vez (p. ej. neural → browser → neural
   * antes de que la primera indexación termine). Cada llamada se numera al
   * entrar; solo la que sigue siendo la última emitida cuando su carga
   * resuelve llega a tocar `total`/`status`/`engineId` — así una resolución
   * tardía de un intento viejo no pisa lo que ya aplicó uno más nuevo (ni
   * dispara una `indexBook` redundante sobre un intento ya abandonado).
   */
  let engineChangeToken = 0

  /**
   * Carga en el motor neural la cola del libro `id`. Si el libro no tenía
   * fragmentos, lo indexa al vuelo (PRD: los importados antes de la v8 del
   * schema no están en `bookIndex`) reusando el pipeline de la búsqueda
   * global, en vez de fallar en seco: el usuario no tiene por qué saber qué
   * es un índice de búsqueda.
   *
   * Devuelve el nº de fragmentos (0 = el EPUB no da texto extraíble), o
   * `null` si `stale()` dice que este intento quedó obsoleto mientras
   * esperaba — en ese caso el llamador no debe tocar nada: hay otro más
   * nuevo gestionando el estado.
   *
   * Ojo: ese número NO significa que el motor tenga montada esa cola (una
   * carga que él abandonó devuelve su cuenta igual). De quién es la cola solo
   * lo dice `queuedBook()`.
   *
   * La comparten `setEngine` (cambio de motor) y `play` (cambio de libro con
   * el motor neural ya activo): son los dos únicos momentos en que la cola
   * puede dejar de corresponder al libro que se quiere escuchar.
   */
  async function loadNeuralQueue(id: string, stale: () => boolean): Promise<number | null> {
    const neural = neuralEngine()
    let count = 0
    try {
      count = (await neural?.load(id)) ?? 0
      if (stale()) return null

      if (count === 0) {
        const book = library.find(id)
        const blob = book ? await repo.getBookFile(book.id) : undefined
        if (book && blob) {
          await search.indexBook(book, blob)
          if (stale()) return null
          count = (await neural?.load(id)) ?? 0
        }
      }
    } catch (e) {
      // Un fallo indexando (EPUB corrupto, cuota llena, etc.) no debe dejar
      // el reproductor en 'loading' para siempre: se trata igual que "sin
      // fragmentos" y se cae al motor navegador.
      console.warn('[audio] no se pudo indexar el libro para la voz neural:', e)
      count = 0
    }
    if (stale()) return null
    // De quién es ahora la cola no se apunta aquí: lo sabe el motor, y el
    // store se lo pregunta con `queuedBook()` cuando lo necesita (ver
    // `neuralEngine`).
    return count
  }

  /**
   * Cambia de motor. Para el actual antes de ceder el mando: dos motores
   * hablando a la vez es el peor bug posible de esta feature.
   */
  async function setEngine(id: AudioEngineId) {
    if (id === engineId.value) return
    const token = ++engineChangeToken
    // Ojo: `listening`, no `status.value === 'playing'`. Este último miente
    // en cuanto un `setEngine` anterior deja `status` en `'loading'` (carga
    // neural en vuelo): el usuario puede haber tocado el selector de motor
    // otra vez antes de que termine, y `status` en ese instante no es
    // `'playing'` aunque la intención de escuchar siga totalmente viva.
    const wasPlaying = listening
    current()?.stop()
    // Igual que en `stop()`: el motor saliente queda en 'idle', no en
    // 'paused'. Nadie puede reanudarlo ya, solo arrancarlo.
    startedEngine = null
    engineId.value = id
    syncEngineFlags()

    if (id === 'neural' && !engines.neural) {
      engines.neural = useAudioBook({
        onChunk: (chunk, pos, count) => {
          chunkId.value = chunk.id
          position.value = pos
          total.value = count
          void persistNeuralProgress(chunk, pos, count)
        },
        onChapter: (href) => {
          chapterHref.value = href
          notifyChapter(href)
        },
        onEnd: () => { stop() },
        // PRD: un fallo de red o de la API no puede limitarse a pausar el
        // reproductor sin decir nada — el usuario vería la escucha pararse
        // sola y no sabría si es su conexión, su saldo o un bug. El error
        // crudo del proveedor es técnico (y puede llevar la API key), así
        // que se humaniza con el mismo traductor que el resto de la IA.
        onError: (e) => {
          pause()
          toaster.error(t('audio.neuralError', { reason: humanizeAiError(e, t) }))
        },
      })
      engines.neural.setRate(rate.value)
      syncEngineFlags()
    }

    if (id === 'neural' && bookId.value) {
      // Se guarda para poder restaurarlo si la carga termina bien pero no
      // toca arrancar nada (ver el `if (!wasPlaying)` de más abajo): 'loading'
      // no puede quedarse pegado en ningún desenlace.
      const priorStatus = status.value
      status.value = 'loading'
      // Play en curso en el momento de empezar. `play()` y `setEngine()` se
      // pelean por el mismo estado (`neuralQueueBookId`, `total`, `status`),
      // así que un `play()` posterior deja tan obsoleto a este intento como
      // otro cambio de motor: sin contarlo, la carga vieja reclamaría la cola
      // para el libro de ANTES —con el motor ya cargado con el nuevo, o sea
      // C2 otra vez— y restauraría `priorStatus`, dejando "pausado" un
      // reproductor que está sonando.
      const seenPlay = playToken
      // Guard de reentrancia: si mientras cargábamos ya se disparó OTRO
      // cambio de motor, este intento quedó obsoleto — ni vale la pena
      // indexar por su cuenta (evita una `indexBook` duplicada) ni tocar el
      // estado que el intento más nuevo ya esté gestionando.
      const count = await loadNeuralQueue(
        bookId.value,
        () => token !== engineChangeToken || seenPlay !== playToken,
      )
      if (count === null) return

      total.value = count

      if (count === 0) {
        // El EPUB no da texto extraíble (ni indexado ni recién indexado): la
        // voz neural no puede leerlo. Volver al motor que sí funciona antes
        // que dejar un reproductor mudo, y avisar del porqué — el PRD exige
        // no fallar en seco.
        //
        // Deliberadamente NO se hace `return` aquí cuando `wasPlaying`: se
        // deja caer al bloque común de más abajo, que ARRANCA el motor
        // navegador recién restaurado. Sin eso, la sesión quedaba "muerta"
        // tras el fallback — mismo bug que se corrigió en la Task 8 (parar
        // con `stop()` deja el estado interno de `useTts` en 'idle', no en
        // 'paused', así que un `resume()` posterior —lo único que ofrece la
        // UI— sería un no-op y el usuario se quedaría sin audio pese a que
        // el motor navegador sigue perfectamente disponible).
        engineId.value = 'browser'
        syncEngineFlags()
        toaster.info(t('audio.neuralNoText'), 8000)

        if (wasPlaying && !engines.browser) {
          // Sonaba, pero el motor navegador ya no está conectado (p. ej. se
          // salió del lector mientras se indexaba): no hay con qué arrancar
          // de nuevo. Se deja como habría quedado un `pause()` real —wake
          // lock liberado y MediaSession avisada— en vez de a medias.
          settleAsPaused()
          return
        }
      }

      if (!wasPlaying) {
        // No sonaba antes de intentar el cambio (haya salido bien o no): el
        // bloque común de abajo no va a arrancar nada. Se restaura el
        // estado previo a este intento —recuperable, como dejaría un
        // `pause()` real— en vez del `'idle'` que forzaría a desaparecer la
        // mini-barra y a perder en silencio una sesión pausada.
        status.value = priorStatus
        return
      }
    }

    if (wasPlaying && bookId.value) {
      // El motor entrante debe ARRANCAR, no reanudar: `resume()` depende
      // del estado interno de cada composable (p. ej. `useTts` solo hace
      // algo si su propio ref interno seguía en 'paused'), y aquí el motor
      // saliente se paró con `stop()` (no `pause()`), así que ese estado ya
      // no es 'paused' en ninguno de los dos motores. `play()` no asume
      // nada sobre el estado previo: el neural cae a reproducir el
      // fragmento ya cargado arriba; el navegador arranca desde la
      // posición actual de la vista (`ttsStart()` dentro de `useTts.play()`),
      // tanto si el motor entrante es el elegido con éxito como si es el
      // navegador recuperado tras un fallback fallido de arriba.
      await play(bookId.value)
    }

    // Red de seguridad: 'loading' nunca debe sobrevivir a `setEngine`, pase
    // lo que pase arriba (p. ej. `play()` de la línea de encima no hizo
    // nada porque, entretanto, el motor de destino se desconectó).
    if (status.value === 'loading') settleAsPaused()
  }

  /**
   * Progreso en porcentaje dados una posición y un total de fragmentos.
   *
   * ponytail: cuenta fragmentos, no palabras ni páginas, así que un capítulo
   * corto pesa igual que uno largo. Si algún día importa, se pondera por
   * `text.length` de cada chunk.
   */
  function percentAt(pos: number, count: number): number {
    if (count === 0) return 0
    return Math.round(((pos + 1) / count) * 100)
  }

  /**
   * Progreso en porcentaje según la posición actual en la cola (refs vivos).
   *
   * Mientras no haya sonado ningún fragmento (`chunkId` sin fijar), `position`
   * no significa nada: el motor arranca por donde diga el progreso guardado
   * (ver `startChunk`), no por el 0 con el que este ref espera. Cerrar la
   * sesión ahí con ese 0 escribiría un `percentEnd` que hace retroceder el
   * libro entero en las stats — pasa con darle a escuchar y cerrar con la ✕
   * mientras se prepara el primer fragmento. Sin nada que sepamos de la
   * escucha, lo cierto es dónde estaba el libro.
   */
  function percentFromPosition(): number {
    if (chunkId.value === null) {
      return (bookId.value ? library.find(bookId.value)?.progress.percent : 0) ?? 0
    }
    return percentAt(position.value, total.value)
  }

  /**
   * Contador de secuencia de `persistNeuralProgress`: cada llamada reabre y
   * re-parsea el EPUB entero con JSZip (`resolveChunkCfi`), así que dos
   * llamadas pueden resolver en desorden (avance rápido, `next()`/`prev()`
   * repetidos, o una descompresión más lenta que la cadencia de
   * fragmentos). Cada invocación se numera al entrar; solo la que sigue
   * siendo la última emitida cuando termina llega a escribir — así una
   * resolución tardía de un fragmento viejo nunca pisa el progreso de uno
   * más nuevo que ya se guardó (o que aún está en vuelo).
   */
  let progressToken = 0

  /**
   * Persiste el punto exacto del libro al avanzar de fragmento, para que
   * retomar leyendo caiga donde se dejó de escuchar. Reutiliza el resolutor
   * de CFI de las citas del RAG: trabaja sobre el blob del EPUB, sin
   * necesidad de una vista renderizada.
   *
   * `pos`/`count` son los del chunk que disparó la llamada (los del propio
   * evento `onChunk`), no los refs vivos `position`/`total`: para cuando
   * esta promesa resuelve, esos refs pueden ya pertenecer a otro fragmento.
   */
  async function persistNeuralProgress(
    chunk: {
      bookId: string
      chapterHref: string | null
      spineIndex: number | null
      text: string
    },
    pos: number,
    count: number,
  ) {
    if (!chunk.chapterHref || chunk.spineIndex === null) return
    const token = ++progressToken
    const [book, blob] = await Promise.all([
      repo.getBook(chunk.bookId),
      repo.getBookFile(chunk.bookId),
    ])
    if (!book || !blob) return
    const cfi = await resolveChunkCfi(blob, {
      chapterHref: chunk.chapterHref,
      spineIndex: chunk.spineIndex,
      snippet: chunk.text,
    })
    if (!cfi) return
    // Guard anti-carrera: si mientras esperábamos ya se emitió un fragmento
    // más nuevo, esta respuesta quedó obsoleta — escribir ahora retrocedería
    // el progreso (o marcaría 'finished' con el CFI de un capítulo anterior).
    if (token !== progressToken) return
    const percent = percentAt(pos, count)
    book.progress = { cfi, percent, lastReadAt: new Date().toISOString() }
    // Escuchar el libro entero cuenta como terminarlo.
    if (percent >= 100) {
      book.status = 'finished'
      book.dateFinished = book.dateFinished ?? new Date().toISOString()
    }
    await repo.saveBook(book)
  }

  /** Revoca el objectURL de portada en uso, si había uno. */
  function releaseArtworkUrl() {
    if (artworkUrl) {
      URL.revokeObjectURL(artworkUrl)
      artworkUrl = null
    }
  }

  /**
   * Rellena `navigator.mediaSession.metadata` con título, autor y portada
   * del libro (PRD: sin esto la pantalla de bloqueo muestra un reproductor
   * anónimo). La portada vive en su propia tabla (v18): se pide aparte y se
   * envuelve en un objectURL, que se revoca antes de crear el siguiente para
   * no acumular uno por cada libro escuchado.
   */
  async function applyNowPlayingMetadata(id: string) {
    const [book, cover] = await Promise.all([repo.getBook(id), repo.getBookCover(id)])
    // Si mientras esperábamos se cambió o cerró la reproducción, esta
    // respuesta ya no aplica: no pisar la metadata del libro nuevo.
    if (bookId.value !== id) return
    releaseArtworkUrl()
    artworkUrl = cover ? URL.createObjectURL(cover) : null
    nowPlaying.setMetadata({
      title: book?.title ?? '',
      author: book?.author ?? '',
      artworkUrl,
    })
  }

  return {
    bookId,
    engineId,
    status,
    rate,
    miniBarHeight,
    isMiniBarVisible,
    sleepMode,
    sleepMinutes,
    position,
    total,
    chunkId,
    chapterHref,
    neuralAvailable,
    engineConnected,
    seekSupported,
    attachEngine,
    detachEngine,
    play,
    pause,
    resume,
    seek,
    next,
    prev,
    stop,
    setRate,
    setEngine,
    setMiniBarHeight,
    setSleepTimer,
    notifyChapter,
    adoptSession,
    releaseSession,
  }
})
